import { FormEvent, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type PaginationMeta = {
  Page: number;
  PerPage: number;
  Total: number;
  TotalPages: number;
};

type PaginationProps = {
  pagination: PaginationMeta;
  disabled?: boolean;
  onChange: (page: number) => void;
};

function visiblePages(current: number, total: number) {
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  return Array.from(pages)
    .filter((page) => page >= 1 && page <= total)
    .sort((a, b) => a - b);
}

export default function Pagination({ pagination, disabled = false, onChange }: PaginationProps) {
  const { Page, PerPage, Total, TotalPages } = pagination;
  const start = Total === 0 ? 0 : (Page - 1) * PerPage + 1;
  const end = Math.min(Page * PerPage, Total);
  const pages = visiblePages(Page, TotalPages);
  const [jumpValue, setJumpValue] = useState("");

  function handleJumpSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = parseInt(jumpValue, 10);
    if (!Number.isFinite(parsed)) return;
    const target = Math.min(Math.max(1, parsed), TotalPages);
    if (target !== Page) onChange(target);
    setJumpValue("");
  }

  return (
    <div className="flex flex-col gap-3 border-t border-[#edf0ee] bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm font-medium text-[#59665f]">
        顯示 <span className="font-bold text-[#203d32]">{start}</span> - <span className="font-bold text-[#203d32]">{end}</span> 筆，
        共 <span className="font-bold text-[#203d32]">{Total}</span> 筆
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-45"
          disabled={disabled || Page <= 1}
          onClick={() => onChange(Page - 1)}
          type="button"
        >
          <ChevronLeft size={16} />
          上一頁
        </button>

        {pages.map((page, index) => {
          const previous = pages[index - 1];
          const hasGap = previous && page - previous > 1;

          return (
            <span className="inline-flex items-center gap-2" key={page}>
              {hasGap && <span className="px-1 text-sm text-[#7b877f]">...</span>}
              <button
                className={`h-9 min-w-9 rounded-md border px-3 text-sm font-bold transition ${
                  page === Page
                    ? "border-[#315746] bg-[#315746] text-white"
                    : "border-[#cfd8d2] bg-white text-[#315746] hover:bg-[#f0f5f2]"
                }`}
                disabled={disabled || page === Page}
                onClick={() => onChange(page)}
                type="button"
              >
                {page}
              </button>
            </span>
          );
        })}

        <button
          className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-45"
          disabled={disabled || Page >= TotalPages}
          onClick={() => onChange(Page + 1)}
          type="button"
        >
          下一頁
          <ChevronRight size={16} />
        </button>

        {TotalPages > 5 && (
          <form className="ml-1 flex items-center gap-1.5 border-l border-[#edf0ee] pl-3" onSubmit={handleJumpSubmit}>
            <label className="text-sm font-medium text-[#59665f]" htmlFor="pagination-jump-page">跳至</label>
            <input
              id="pagination-jump-page"
              className="h-9 w-16 rounded-md border border-[#cfd8d2] px-2 text-center text-sm outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15 disabled:bg-[#f7f9f8] disabled:text-[#9a8a7a]"
              disabled={disabled}
              inputMode="numeric"
              max={TotalPages}
              min={1}
              placeholder={String(Page)}
              type="number"
              value={jumpValue}
              onChange={(event) => setJumpValue(event.target.value)}
            />
            <span className="whitespace-nowrap text-sm text-[#59665f]">/ {TotalPages} 頁</span>
            <button
              className="inline-flex h-9 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-45"
              disabled={disabled || !jumpValue}
              type="submit"
            >
              前往
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
